export const SERVICE_TYPES = ["drop_off", "full_service", "event_space", "pickup"] as const;
export type ServiceType = (typeof SERVICE_TYPES)[number];

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  drop_off: "Drop-Off Catering",
  full_service: "Full-Service (Chef In-House)",
  event_space: "Event Space",
  pickup: "Pickup",
};

/** Compact labels for filters and chips. Typed against ServiceType so a new
 * service type can't ship without one (#872). */
export const SERVICE_TYPE_SHORT_LABELS: Record<ServiceType, string> = {
  drop_off: "Drop-off",
  full_service: "Full Service",
  event_space: "Event Space",
  pickup: "Pickup",
};

type Maybe = string | null | undefined;

/** service_type is the source of truth for how the food gets there (#873). /order
 * pickups placed before #873 were saved as drop_off + fulfillment_type 'pickup', so
 * a pickup fulfillment_type also reads as Pickup. */
export function effectiveServiceType<T extends Maybe>(serviceType: T, fulfillmentType?: Maybe): T | "pickup" {
  return fulfillmentType === "pickup" ? "pickup" : serviceType;
}

/** Display label for a quote's service, read through effectiveServiceType. */
export function serviceTypeLabel(serviceType: string, fulfillmentType?: Maybe): string {
  return SERVICE_TYPE_LABELS[effectiveServiceType(serviceType, fulfillmentType) as ServiceType] ?? serviceType;
}

/** The quotes.fulfillment_type the admin form writes for a service type (#873).
 * Null where it doesn't apply (on-site service), per the column's own comment. */
export function fulfillmentTypeFor(serviceType: Maybe): "pickup" | "delivery" | null {
  if (serviceType === "pickup") return "pickup";
  if (serviceType === "drop_off") return "delivery";
  return null;
}

/** Pickup quotes have no delivery address or delivery charge (#818). */
export function isPickup(serviceType: Maybe, fulfillmentType?: Maybe): boolean {
  return effectiveServiceType(serviceType, fulfillmentType) === "pickup";
}

/** Label for event_start_time input/display based on service_type. */
export function getEventTimeLabel(serviceType: Maybe, fulfillmentType?: Maybe): string {
  if (isPickup(serviceType, fulfillmentType)) return "Pickup Time";
  if (serviceType === "drop_off") return "Delivery Time";
  return "Event Start Time";
}

/** Format a Postgres `time` string ("18:00" or "18:00:00") as "6:00 PM". */
export function formatEventStartTime(time: string | null | undefined): string {
  if (!time) return "";
  const parts = time.split(":");
  if (parts.length < 2) return "";
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) return "";
  const period = h >= 12 ? "PM" : "AM";
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayHour}:${m.toString().padStart(2, "0")} ${period}`;
}

export const QUOTE_STATUSES = ["draft", "sent", "accepted", "declined"] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  accepted: "Accepted",
  declined: "Declined",
};

// Lean sales pipeline (issue #423). 'won' is the archived terminal (replaces the
// old event_complete/completed). 'lost' is the orthogonal is_lost boolean — not a
// stored stage value — so it is intentionally absent from this list.
export const PIPELINE_STAGES = [
  "new",
  "qualifying",
  "quote_sent",
  "deposit_pending",
  "booked",
  "won",
] as const;
export type PipelineStage = (typeof PIPELINE_STAGES)[number];

export const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  new: "New",
  qualifying: "Qualifying",
  quote_sent: "Quote Sent",
  deposit_pending: "Deposit Pending",
  booked: "Booked",
  won: "Won",
};

export const PIPELINE_STAGE_COLORS: Record<PipelineStage, string> = {
  new: "bg-muted text-muted-foreground",
  // Gold — signals "we owe the customer a quote".
  qualifying: "bg-[hsl(42,78%,60%)]/15 text-[hsl(42,78%,30%)] border-[hsl(42,78%,60%)]/40",
  quote_sent: "bg-primary/10 text-primary border-primary/30",
  // Amber/warning — the deposit is the conversion gate; this column needs chasing.
  deposit_pending: "bg-warning/20 text-warning border-warning/40",
  booked: "bg-success/20 text-success-foreground border-success/40",
  won: "bg-primary/20 text-primary border-primary/40",
};

export const PIPELINE_STAGE_DESCRIPTIONS: Record<PipelineStage, string> = {
  new: "Brand-new inquiry — nobody has contacted them yet. Reach out immediately (first attempt within 24 hours).",
  qualifying: "In touch and gathering event details; no quote sent yet. Nail down the details, then send the quote.",
  quote_sent: "A quote has been sent; awaiting the client's decision. Follow up here until it's a yes or no. Moves here automatically when status changes to 'Sent'.",
  deposit_pending: "Client accepted — now chase the deposit. Moves here automatically when the quote status is set to 'Accepted'.",
  booked: "Deposit paid and the event is confirmed. Moves here automatically when a payment is logged.",
  won: "Event delivered and closed out. Move here manually after the event is complete.",
};

export interface PipelineStageSop {
  /** What's true when a record sits in this stage (entry condition). */
  means: string;
  /** The action expected of the user here (imperative). */
  do: string;
  /** The exact condition to move it on, and to which stage. */
  advanceWhen: string;
  /** The timing / SLA expectation. */
  clock: string;
}

/**
 * Action-oriented standard operating procedure per pipeline stage, surfaced in
 * the (i) popover on each stage of the quote detail stepper (#381). Complements
 * PIPELINE_STAGE_DESCRIPTIONS (which describes stage *mechanics*) with what the
 * user is expected to *do*. Copy is the verbatim text agreed in #381.
 */
export const PIPELINE_STAGE_SOP: Record<PipelineStage, PipelineStageSop> = {
  new: {
    means: "Brand-new inquiry; nobody has contacted them yet.",
    do: "Reach out immediately via GHL — call first, then text/email; confirm the event basics.",
    advanceWhen: "→ Qualifying once you've made contact and are gathering details.",
    clock: "First contact attempt no later than 24 hours after the lead arrives — sooner is always better; never let it go stale.",
  },
  qualifying: {
    means: "You're in touch and figuring out their needs; no quote sent yet.",
    do: "Nail down date, guest count, service type, venue, and budget; build the quote.",
    advanceWhen: "→ Quote Sent automatically when you send the quote (status → Sent).",
    clock: "Quote out within 1 business day of having the details.",
  },
  quote_sent: {
    means: "The quote is in their inbox; the ball is in their court. Follow-ups live here.",
    do: "Confirm receipt, answer questions, and work a follow-up cadence until you get a clear yes or no.",
    advanceWhen: "→ Deposit Pending on a yes; mark Lost on a no.",
    clock: "Confirm receipt the same day; then follow up every 2 business days, up to 3 attempts.",
  },
  deposit_pending: {
    means: "Client accepted (verbal yes); not yet paid.",
    do: "Send the deposit invoice, lock the date, confirm contract details.",
    advanceWhen: "→ Booked automatically when a payment is recorded.",
    clock: "Deposit request out within 24 hours of acceptance.",
  },
  booked: {
    means: "Deposit paid; the event is on the books.",
    do: "Finalize logistics — headcount, timeline, staffing, menu lock; collect the balance.",
    advanceWhen: "→ Won after the event is delivered and the balance is settled.",
    clock: "Details confirmed 1 week before the event.",
  },
  won: {
    means: "Event delivered, balance paid, closed out.",
    do: "Request a review or testimonial, then archive.",
    advanceWhen: "Terminal stage.",
    clock: "—",
  },
};

export interface EventType {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

export interface Package {
  id: string;
  name: string;
  description: string | null;
  pricing_type: "per_person" | "flat_rate";
  price: number;
  min_guests: number | null;
  max_guests: number | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  items?: PackageItem[];
  event_types?: EventType[];
}

export interface PackageItem {
  id: string;
  package_id: string;
  menu_item_id: string;
  quantity: number;
  notes: string | null;
}

export interface Quote {
  id: string;
  quote_number: string;
  client_name: string;
  client_email: string | null;
  client_phone: string | null;
  event_type_id: string | null;
  event_date: string | null;
  service_type: ServiceType;
  guest_count: number;
  status: QuoteStatus;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  discount_amount: number;
  delivery_amount: number;
  service_charge_amount?: number;
  total: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
  event_type?: EventType;
  line_items?: QuoteLineItem[];
}

export const PAYMENT_METHODS = ["cash", "etransfer", "credit_card", "check", "square", "other"] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: "Cash",
  etransfer: "E-Transfer",
  credit_card: "Credit Card",
  check: "Check",
  square: "Square",
  other: "Other",
};

export interface QuotePayment {
  id: string;
  quote_id: string;
  amount: number;
  method: string;
  reference: string | null;
  notes: string | null;
  recorded_by: string | null;
  payment_date: string;
  created_at: string;
  recorded_by_profile?: { display_name: string | null } | null;
}

export interface QuoteLineItem {
  id: string;
  quote_id: string;
  menu_item_id: string | null;
  package_id: string | null;
  name: string;
  description: string | null;
  pricing_type: string;
  unit_price: number;
  quantity: number;
  line_total: number;
  sort_order: number;
}

export type DocumentLabel = "Estimate" | "Quote" | "Invoice";

export function getDocumentLabel(status: string, paymentStatus?: string): DocumentLabel {
  if (status === "draft") return "Estimate";
  if (status === "accepted" && paymentStatus && paymentStatus !== "unpaid") return "Invoice";
  return "Quote";
}
